/**
 * Builds the "Description" cell for the alert insights from the raw
 * `alertCustomDetails` object the API now returns.
 *
 * This mapping used to live on the server (`api-insights` `utils/alarms.ts`), but the
 * temperature branch has to follow the viewer's dashboard units, which only the client
 * knows. The server-side helper is still used for the CSV exports, which are fixed to
 * Fahrenheit to match the temperature history export.
 *
 * Behavior lists mirror the server's — keep the two in sync.
 */
import type { AlertCustomDetails } from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions/temperatureUtils'

const FRIDGE_ALARM_BEHAVIORS = [
  'fridge alarms yellow only',
  'fridge alarms red only',
  'fridge alarms red and yellow only',
  'fridge alarms green only',
  'fridge alarms (includes all red, yellow, green)',
]
const FUEL_BEHAVIORS = ['low fuel']
const TEMPERATURE_BEHAVIORS = [
  'monitoring point temperature',
  'monitoring point temperature - outside dc - fridge on',
  'virtual setpoint-based monitoring point temperature',
]
const DOOR_BEHAVIORS = ['door']
const GEOFENCE_BEHAVIORS = ['geofence entry', 'geofence exit', 'left geofence with door open']

const isBehavior = (behaviors: string[], behavior?: string): boolean =>
  behavior ? behaviors.includes(behavior.trim().toLowerCase()) : false

interface BuildAlertDescriptionArgs {
  behavior?: string
  alertCustomDetails?: AlertCustomDetails
  units: 'Imperial' | 'Metric'
}

export const buildAlertDescription = ({ behavior, alertCustomDetails, units }: BuildAlertDescriptionArgs): string => {
  const details = alertCustomDetails

  if (isBehavior(FRIDGE_ALARM_BEHAVIORS, behavior)) {
    const { description, key } = details?.fridgeAlarmInfo ?? {}
    return description && key ? `${description} (${key.split(':')[1] ?? key})` : ''
  }

  if (isBehavior(FUEL_BEHAVIORS, behavior)) {
    const key = details?.fuelPercentage
    return key ? `Fuel %: ${key.replace(/%$/, '')}` : ''
  }

  if (isBehavior(TEMPERATURE_BEHAVIORS, behavior)) {
    const celsius = details?.temperature
    if (typeof celsius !== 'number' || !Number.isFinite(celsius)) {
      return ''
    }
    return `Temperature: ${celsiusToUser(celsius, 1, units)}`
  }

  if (isBehavior(DOOR_BEHAVIORS, behavior)) {
    const key = details?.doorState
    return key ? `Door: ${key}` : ''
  }

  if (isBehavior(GEOFENCE_BEHAVIORS, behavior)) {
    const name = details?.geofence
    return name ? `Geofence: ${name}` : ''
  }

  return ''
}
